/* eslint-disable react/prop-types */
import { useMemo, useState } from 'react';
import { ArrowUpRight, CircleGauge, Settings2 } from 'lucide-react';
import { Button } from 'src/features/tyre-management/components/ui/button';
import StatePill from './StatePill';

const colour = (position) => {
  const fitment = position.current_fitment;
  if (!fitment) return '#94A3B8';
  if (['critical', 'overdue', 'failed', 'damaged'].includes(fitment.alert_state) || ['failed', 'damaged'].includes(fitment.condition)) return '#C2413B';
  if (['warning', 'due'].includes(fitment.alert_state)) return '#D97706';
  return '#168C6B';
};

const TyrePlan = ({
  section, onNavigate, canConfigure, canOpenWorkspace
}) => {
  const [selectedId, setSelectedId] = useState(null);
  const configuration = section?.data?.configuration;
  const positions = section?.data?.layout?.positions || configuration?.positions || [];
  const selected = positions.find((position) => position.id === selectedId);
  const bounds = useMemo(() => {
    if (!positions.length) {
      return {
        minX: 0, maxX: 100, minY: 0, maxY: 100
      };
    }
    const x = positions.map((item) => Number(item.x));
    const y = positions.map((item) => Number(item.y));
    return {
      minX: Math.min(...x), maxX: Math.max(...x), minY: Math.min(...y), maxY: Math.max(...y)
    };
  }, [positions]);

  if (section?.data?.variant === 'v2_setup_required') {
    return (
      <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed border-amber-200 bg-amber-50/60 p-8 text-center">
        <span className="rounded-full bg-white p-3 text-amber-600 shadow-sm"><Settings2 className="h-6 w-6" aria-hidden="true" /></span>
        <h3 className="mt-4 text-base font-bold text-slate-950">Tyre layout needs setup</h3>
        <p className="mt-1 max-w-sm text-sm leading-6 text-slate-500">{section.reason}</p>
        {canConfigure && section.data.action && <Button className="mt-5" onClick={() => onNavigate(section.data.action)}>Open vehicle setup</Button>}
      </div>
    );
  }

  if (section?.data?.variant === 'legacy') {
    return (
      <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed border-amber-200 bg-amber-50/60 p-8 text-center">
        <CircleGauge className="h-7 w-7 text-amber-600" aria-hidden="true" />
        <StatePill value="warning" label="Legacy tyre records" />
        <h3 className="mt-4 text-base font-bold text-slate-950">
          {section.data.legacy_mapping_count || 0}
          {' '}
          loose active mapping(s)
        </h3>
        <p className="mt-1 text-sm text-slate-500">Legacy mappings do not verify spatial position or V2 occupancy.</p>
        {section.data.action && <Button className="mt-5" variant="outline" onClick={() => onNavigate(section.data.action)}>Open legacy records</Button>}
      </div>
    );
  }

  if (section?.data?.variant === 'disabled') {
    return (
      <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50/70 p-8 text-center">
        <CircleGauge className="h-7 w-7 text-slate-400" aria-hidden="true" />
        <h3 className="mt-4 text-base font-bold text-slate-950">Tyre Management is not enabled</h3>
        <p className="mt-1 text-sm text-slate-500">No tyre-health claim is shown for this vehicle.</p>
      </div>
    );
  }

  const width = Math.max(bounds.maxX - bounds.minX, 1);
  const height = Math.max(bounds.maxY - bounds.minY, 1);
  const projectX = (x) => 70 + ((Number(x) - bounds.minX) / width) * 260;
  const projectY = (y) => 44 + ((Number(y) - bounds.minY) / height) * 230;

  return (
    <div className="vd-tyre-plan">
      <div className="vd-tyre-plan__meta">
        <div>
          <p className="vd-eyebrow">Tyre system</p>
          <h2>{section.data.layout.name}</h2>
        </div>
        <StatePill value={section.data.worst_condition} />
      </div>
      <div className="vd-tyre-plan__stage">
        <svg viewBox="0 0 400 320" role="img" aria-label={`${configuration?.vehicle_number || 'Vehicle'} tyre position plan`}>
          <rect x="145" y="28" width="110" height="260" rx="24" className="vd-chassis" />
          <line x1="200" y1="18" x2="200" y2="302" className="vd-centre-line" />
          {positions.map((position) => {
            const x = projectX(position.x);
            const y = projectY(position.y);
            const active = selectedId === position.id;
            return (
              <g
                key={position.id}
                role="button"
                tabIndex="0"
                aria-label={`${position.label}: ${position.current_fitment?.tyre_id || 'empty'}`}
                onClick={() => setSelectedId(position.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') setSelectedId(position.id);
                }}
                className="vd-wheel"
              >
                <rect x={x - 18} y={y - 10} width="36" height="20" rx="6" fill={colour(position)} stroke={active ? '#102A43' : '#FFFFFF'} strokeWidth={active ? 4 : 2} />
                <text x={x} y={y + 29}>{position.code}</text>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="vd-tyre-plan__footer">
        <div>
          <span className="vd-mono vd-reading-small">
            {section.data.layout.occupied_positions}
            /
            {section.data.layout.mandatory_positions}
          </span>
          <span> positions fitted</span>
        </div>
        <div>
          {section.data.open_alert_count}
          {' '}
          open alert
          {section.data.open_alert_count === 1 ? '' : 's'}
        </div>
      </div>
      {selected && (
        <div className="vd-position-detail" aria-live="polite">
          <div>
            <strong>{selected.label}</strong>
            <span className="vd-mono">{selected.code}</span>
          </div>
          {selected.current_fitment ? (
            <div>
              <span>{selected.current_fitment.tyre_id}</span>
              <StatePill value={selected.current_fitment.alert_state} />
            </div>
          ) : <span>Position is empty</span>}
        </div>
      )}
      {canOpenWorkspace && section.data.action && (
      <Button className="mt-4 w-full" variant="outline" onClick={() => onNavigate(section.data.action)}>
        Open tyre workspace
        <ArrowUpRight />
      </Button>
      )}
    </div>
  );
};

export default TyrePlan;
